/**
 * 안전자재 허브 분야 (물품별 가이드 — 법령·질의회시 등 복합 출처)
 */
import { SMART_EQUIPMENT_BOARD_POST_PATH } from './smartEquipmentSource';

export const SAFETY_RULES_LAW_ID = '007363';

/** @typedef {{ text: string, to: string }} CategoryCheckpoint */

export const safetyMaterialsCategories = [
  {
    id: 'ppe',
    title: '안전용품·보호구',
    subtitle: '착용 보호구',
    description:
      '안전모, 안전화, 보안경, 마스크 등 품목별 법적 요건·착용·관리기준과 관련 법령·질의회시를 한 화면에서 확인합니다.',
    icon: 'shield',
    status: 'available',
    path: '/safety-materials/ppe',
    keywords: ['보호구', '안전모', '안전화', '착용'],
    filterTag: '보호구',
    checkpoints: [
      { text: '안전모·안전화 착용 의무', to: '/safety-materials/ppe?equipment=helmet#related-laws' },
      { text: '보호구 안전인증(제74조)', to: '/safety-regulations/003786/article/74' },
      {
        text: '관리비 인정 품목',
        to: '/safety-materials/ppe?equipment=helmet',
      },
    ],
    actionLabel: '보호구 가이드 바로가기',
    badgeClass: 'bg-amber-100 text-amber-900 border-amber-300',
    accentClass: 'border-amber-200 hover:border-amber-400',
  },
  {
    id: 'scaffold',
    title: '비계·작업발판',
    subtitle: '가설 구조물',
    description:
      '강관비계, 작업발판, 안전난간 등 현장 가설물의 설치·사용·점검 기준을 품목별로 바로 확인합니다.',
    icon: 'layers',
    status: 'available',
    path: '/safety-materials/scaffold',
    keywords: ['비계', '작업발판', '강관비계'],
    filterTag: '가설구조물',
    checkpoints: [
      { text: '시스템비계 설치 기준', to: '/safety-materials/scaffold?item=systemScaffold#related-laws' },
      { text: '안전난간·발끝막이 규정', to: '/safety-materials/scaffold?item=guardrail#related-laws' },
      { text: '현장 점검 체크포인트', to: '/safety-materials/scaffold?item=tubeScaffold' },
    ],
    actionLabel: '비계·발판 가이드 바로가기',
    badgeClass: 'bg-slate-100 text-slate-800 border-slate-300',
    accentClass: 'border-slate-200 hover:border-slate-400',
  },
  {
    id: 'fall',
    title: '추락·붕괴 방지',
    subtitle: '개구부·안전대',
    description:
      '안전대, 추락방지기, 개구부 방호 등 추락·붕괴 위험 작업 품목의 법적 기준과 사용 요건을 확인합니다.',
    icon: 'alert',
    status: 'available',
    path: '/safety-materials/fall',
    keywords: ['추락', '안전대', '개구부'],
    filterTag: '추락방지',
    checkpoints: [
      { text: '전신형 안전대 사용 기준', to: '/safety-materials/fall?item=fullBodyHarness#related-laws' },
      { text: '개구부 덮개·난간 의무', to: '/safety-materials/fall?item=openingCover#related-laws' },
      { text: '추락방지기 점검', to: '/safety-materials/fall?item=fallArrester' },
    ],
    actionLabel: '추락방지 가이드 바로가기',
    badgeClass: 'bg-orange-100 text-orange-900 border-orange-300',
    accentClass: 'border-orange-200 hover:border-orange-400',
  },
  {
    id: 'machine',
    title: '기계·기구·설비',
    subtitle: '방호장치·운전',
    description:
      '프레스, 연삭기, 컨베이어 등 기계·설비의 방호장치와 운전·정비 시 안전조치를 품목별로 확인합니다.',
    icon: 'cog',
    status: 'available',
    path: '/safety-materials/machine',
    keywords: ['기계', '방호장치', '설비'],
    filterTag: '방호장치',
    checkpoints: [
      { text: '프레스·연삭기 방호장치', to: '/safety-materials/machine?item=press#related-laws' },
      { text: '위험부 덮개·인터록', to: '/safety-materials/machine?item=grinder#related-laws' },
      { text: '운전 전 점검 항목', to: '/safety-materials/machine?item=conveyor' },
    ],
    actionLabel: '기계·설비 가이드 바로가기',
    badgeClass: 'bg-gray-100 text-gray-800 border-gray-300',
    accentClass: 'border-gray-200 hover:border-gray-400',
  },
  {
    id: 'electric',
    title: '전기·화재·위험물',
    subtitle: '전기작업·방폭',
    description:
      '용접기, 소화기, 가스측정기 등 전기·화재·위험물 품목의 사용·설치·점검 가이드를 바로 확인합니다.',
    icon: 'zap',
    status: 'available',
    path: '/safety-materials/electric',
    keywords: ['전기', '화재', '위험물'],
    filterTag: '전기·화재',
    checkpoints: [
      { text: '누전차단·접지 기준', to: '/safety-materials/electric?item=groundFault#related-laws' },
      { text: '소화기 비치·점검', to: '/safety-materials/electric?item=fireExtinguisher#related-laws' },
      { text: '밀폐공간 가스측정', to: '/safety-materials/electric?item=gasDetector' },
    ],
    actionLabel: '전기·화재 가이드 바로가기',
    badgeClass: 'bg-yellow-100 text-yellow-900 border-yellow-300',
    accentClass: 'border-yellow-200 hover:border-yellow-400',
  },
  {
    id: 'lifting',
    title: '양중·하역',
    subtitle: '크레인·중량물',
    description:
      '크레인, 줄걸이, 지게차 등 양중·하역 장비의 사용·신호·점검 기준을 품목별로 확인합니다.',
    icon: 'crane',
    status: 'available',
    path: '/safety-materials/lifting',
    keywords: ['양중', '크레인', '하역'],
    filterTag: '양중·하역',
    checkpoints: [
      { text: '크레인 신호·운전 요건', to: '/safety-materials/lifting?item=mobileCrane#related-laws' },
      { text: '와이어로프·슬링 사용', to: '/safety-materials/lifting?item=wireRopeSling#related-laws' },
      { text: '지게차 하역 안전', to: '/safety-materials/lifting?item=forklift' },
    ],
    actionLabel: '양중·하역 가이드 바로가기',
    badgeClass: 'bg-teal-100 text-teal-900 border-teal-300',
    accentClass: 'border-teal-200 hover:border-teal-400',
  },
  {
    id: 'smart',
    title: '스마트 안전장비',
    subtitle: 'IoT·AI 기반',
    description: '14종 단위장비를 품목별로 안내합니다. 개요·활용·관리비·관련 법령을 확인할 수 있습니다.',
    icon: 'cpu',
    status: 'available',
    path: '/safety-materials/smart',
    keywords: ['스마트', 'AI CCTV', '스마트헬멧', 'IoT', '안전장비', '에어백조끼'],
    filterTag: '스마트안전',
    checkpoints: [
      { text: 'AI CCTV·영상 분석', to: '/safety-materials/smart?item=aiCctv#related-laws' },
      { text: '스마트 헬멧·착용형 장비', to: '/safety-materials/smart?item=workerWearableSmart' },
      { text: '가이드라인 PDF·지원 안내', to: SMART_EQUIPMENT_BOARD_POST_PATH },
    ],
    actionLabel: '스마트 안전장비 가이드 바로가기',
    badgeClass: 'bg-indigo-100 text-indigo-900 border-indigo-300',
    accentClass: 'border-indigo-200 hover:border-indigo-400',
  },
];

export const getCategoryById = (id) =>
  safetyMaterialsCategories.find((c) => c.id === id);

export const getCheckpointTexts = (cat) =>
  (cat.checkpoints || []).map((cp) => (typeof cp === 'string' ? cp : cp.text));
